import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import accountApiClient, {
  ApplicationTypeEnum,
  LoginRequest,
  LoginTwoFactorAuthRequest,
  RestorePasswordExpirationModel,
  RestorePasswordModel,
  TokenResponse,
  UserCodeModel,
} from 'api/accountApiClient';
import backofficeApiClient, {
  BackofficeUserModel,
  BOUserBullhornSessionViewModel,
  InitialProfileRequestModel,
  LoginRequestModel,
  RegInvitedUserModel,
  RegRecruiterModel,
  UpdateTwoFactorAuthStatusRequestModel,
} from 'api/backofficeApiClient';
import { startUserLiveChat, startVisitorLiveChat } from 'libs/Intercom';
import { checkIsMultiAccount } from 'utils/userRolesUtils';
import { UserRole } from '../../enums';

export interface AuthState {
  user: BackofficeUserModel | null;
  activeRole?: UserRole | null;
}

const initialState: AuthState = {
  user: null,
  activeRole: null,
};

const login = createAsyncThunk('auth/login', async (data: LoginRequest) => {
  const response = await accountApiClient.login(data);

  if (!response.token) return response;

  saveToken(response);

  return await backofficeApiClient.getMyUserProfile();
});

const handleTokenRefresh = async (accessToken: string) => {
  const response = await accountApiClient.refreshToken({
    accessToken,
  });

  saveToken(response);

  return response;
};

const refreshToken = createAsyncThunk('auth/refreshToken', handleTokenRefresh);

const getUserDetails = createAsyncThunk('auth/getUserDetails', async () => {
  return await backofficeApiClient.getMyUserProfile();
});

const signUp = createAsyncThunk(
  'auth/signUp',
  async (data: RegRecruiterModel) => {
    return await backofficeApiClient.createNewAccount(data);
  },
);

const createInvitedUserAccount = createAsyncThunk(
  'auth/createInvitedUserAccount',
  async (data: RegInvitedUserModel) => {
    return await backofficeApiClient.createInvitedUserAccount(data);
  },
);

const sendConfirmationAgain = createAsyncThunk(
  'auth/sendRegistrationEmailAgain',
  async (email: string) => {
    return await accountApiClient.sendRegistrationConfirmationAgain(
      email,
      ApplicationTypeEnum.Backoffice,
    );
  },
);

const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string) => {
    return await accountApiClient.forgotPassword({
      email,
      applicationType: ApplicationTypeEnum.Backoffice,
    });
  },
);

const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: RestorePasswordModel) => {
    return await accountApiClient.restorePassword(data);
  },
);

const confirmAccount = createAsyncThunk(
  'auth/confirmAccount',
  async (data: UserCodeModel) => {
    return await accountApiClient.confirmRegistration(data);
  },
);

const saveProfileSetup = createAsyncThunk(
  'auth/saveProfileSetup',
  async (data: InitialProfileRequestModel) => {
    await backofficeApiClient.setupInitialProfile(data);

    const jwtToken = localStorage.getItem('jwt-token');

    return handleTokenRefresh(jwtToken as string);
  },
);

const checkIsResetPasswordLinkExpired = createAsyncThunk(
  'auth/checkIsResetPasswordLinkExpired',
  async (data: RestorePasswordExpirationModel) => {
    return await accountApiClient.isExpiredRestorePassLink(data);
  },
);

const getAvailableRolesForRegistration = createAsyncThunk(
  'auth/getAvailableRolesForRegistration',
  async () => {
    return backofficeApiClient.getProfileTypes();
  },
);

const loginWithTwoFA = createAsyncThunk(
  'auth/loginWithTwoFA',
  async (data: LoginTwoFactorAuthRequest, { rejectWithValue }) => {
    try {
      const response = await accountApiClient.login_two_factor_auth_(data);

      saveToken(response);

      return await backofficeApiClient.getMyUserProfile();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const updateStatusTwoFA = createAsyncThunk(
  'account/updateStatusTwoFA',
  async (data: UpdateTwoFactorAuthStatusRequestModel, { rejectWithValue }) => {
    try {
      const response =
        await backofficeApiClient.updateTwoFactorAuthStatus(data);

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const bullhornDisconnect = createAsyncThunk(
  'account/bullhornDisconnect',
  async () => {
    await backofficeApiClient.disconnectAccount();

    removeBullhornToken();
  },
);

const connectBullhornAccount = createAsyncThunk(
  'account/connectBullhornAccount',
  async (data: LoginRequestModel) => {
    const response = await backofficeApiClient.connectAccount(data);

    saveBullhornToken(response);

    return response;
  },
);

const saveBullhornToken = (response: BOUserBullhornSessionViewModel) => {
  localStorage.setItem('bullhorn-token', JSON.stringify(response));
};

const removeBullhornToken = () => {
  localStorage.removeItem('bullhorn-token');
};

const saveToken = (response: TokenResponse) => {
  const { token } = response;

  localStorage.setItem('jwt-token', token || '');
};

const handleLogin = (state: AuthState, user: BackofficeUserModel) => {
  state.user = user;
  let activeRole;

  if (checkIsMultiAccount(user.roles)) {
    const currentActiveRole = localStorage.getItem('active-role');

    if (currentActiveRole) {
      activeRole = currentActiveRole as UserRole;
      state.activeRole = activeRole;
    } else {
      activeRole = UserRole.Admin;
      state.activeRole = activeRole;
      saveActiveRole(UserRole.Admin);
    }
  }

  startUserLiveChat(user, activeRole);
};

const handleBullhornEnableChange = (
  state: AuthState,
  bullhornEnable: boolean,
) => {
  state.user = { ...state.user, bullhornEnable };
};

const saveActiveRole = (role: UserRole) => {
  localStorage.setItem('active-role', role);
};

const removeActiveRole = () => {
  localStorage.removeItem('active-role');
};

const handleLogout = async (state: AuthState) => {
  state.user = null;
  state.activeRole = null;
  try {
    localStorage.getItem('jwt-token') && (await accountApiClient.logout());
  } catch (e) {
  } finally {
    localStorage.removeItem('jwt-token');
    removeActiveRole();
    removeBullhornToken();
    startVisitorLiveChat();
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      handleLogout(state);
    },
    switchActiveRole(state) {
      if (state.user?.roles && Array.isArray(state.user.roles)) {
        const userRolesCopy = { roles: [...state.user.roles] };

        const determineNewRole = () => {
          return userRolesCopy.roles.includes(UserRole.CorporateTA)
            ? UserRole.CorporateTA
            : UserRole.RecruiterCompany;
        };

        const newActiveRole =
          state.activeRole === UserRole.Admin
            ? determineNewRole()
            : UserRole.Admin;

        state.activeRole = newActiveRole;
        saveActiveRole(newActiveRole);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      !(action.payload as TokenResponse)?.authCode &&
        handleLogin(state, action.payload as BackofficeUserModel);
    });
    builder.addCase(login.rejected, (state, action) => {
      handleLogout(state);
    });
    builder.addCase(loginWithTwoFA.fulfilled, (state, action) => {
      handleLogin(state, action.payload);
    });
    builder.addCase(loginWithTwoFA.rejected, (state, action) => {
      handleLogout(state);
    });
    builder.addCase(refreshToken.rejected, (state, action) => {
      handleLogout(state);
    });
    builder.addCase(getUserDetails.fulfilled, (state, action) => {
      handleLogin(state, action.payload);
    });
    builder.addCase(getUserDetails.rejected, (state, action) => {
      handleLogout(state);
    });
    builder.addCase(bullhornDisconnect.fulfilled, (state) => {
      handleBullhornEnableChange(state, false);
    });
    builder.addCase(connectBullhornAccount.fulfilled, (state) => {
      handleBullhornEnableChange(state, true);
    });
    builder.addCase(updateStatusTwoFA.fulfilled, (state, action) => {
      const is2FAStatus = state?.user?.isBOTwoFactorAuthStatus;
      state.user = {
        ...state.user,
        isBOTwoFactorAuthStatus: !is2FAStatus,
      };
    });
  },
});

const { actions, reducer } = authSlice;
const { logout, switchActiveRole } = actions;

export {
  switchActiveRole,
  login,
  refreshToken,
  getUserDetails,
  logout,
  signUp,
  saveProfileSetup,
  sendConfirmationAgain,
  forgotPassword,
  resetPassword,
  confirmAccount,
  getAvailableRolesForRegistration,
  checkIsResetPasswordLinkExpired,
  createInvitedUserAccount,
  loginWithTwoFA,
  updateStatusTwoFA,
  bullhornDisconnect,
  connectBullhornAccount,
};
export default reducer;
